import type { Phase } from '../check-session';
import { RESULT_LABEL, reasonText } from '../labels';
import type { Candidate } from '../types';
import { CandidateList } from './CandidateList';

interface Props {
  phase: Phase;
  onDismiss: () => void;
  onConfirm: (candidate: Candidate) => void;
  onRetry: () => void;
}

export function ResultOverlay({ phase, onDismiss, onConfirm, onRetry }: Props) {
  if (phase.kind === 'idle') return null;

  if (phase.kind === 'pending') {
    return (
      <div class="overlay overlay--pending" role="status" aria-live="polite">
        <div class="overlay__icon">…</div>
        <div class="overlay__word">Prüfe</div>
        <div class="overlay__query">{phase.query}</div>
      </div>
    );
  }

  // Netzfehler: bewusst grau und mit eigener Wortwahl. Sähe er aus wie ROT,
  // würden berechtigte Personen abgewiesen, weil das WLAN hakt.
  if (phase.kind === 'offline') {
    return (
      <div class="overlay overlay--offline" role="alert">
        <div class="overlay__icon">⚠</div>
        <div class="overlay__word">Keine Verbindung</div>
        <div class="overlay__reason">Nicht geprüft. Bitte erneut versuchen</div>
        <div class="overlay__query">{phase.query}</div>
        <div class="overlay__actions">
          <button class="btn btn--primary" onClick={onRetry}>
            Erneut versuchen
          </button>
          <button class="btn" onClick={onDismiss}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <div class="overlay overlay--offline" role="alert">
        <div class="overlay__icon">⚠</div>
        <div class="overlay__word">Nicht geprüft</div>
        <div class="overlay__reason">{phase.message}</div>
        <div class="overlay__actions">
          <button class="btn" onClick={onDismiss}>
            Schliessen
          </button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'locked-out') {
    return (
      <div class="overlay overlay--offline" role="alert">
        <div class="overlay__icon">⚠</div>
        <div class="overlay__word">Gerät gesperrt</div>
        <div class="overlay__reason">Bitte beim Office neu anmelden</div>
      </div>
    );
  }

  const { response } = phase;
  const label = RESULT_LABEL[response.result];
  const reason = reasonText(response.reason);
  const person = response.person;

  return (
    <div class={`overlay overlay--${response.result}`} role="alert">
      {/* Symbol und Wort neben der Farbe. Sonnenlicht und Farbsehschwäche. */}
      <div class="overlay__icon" aria-hidden="true">
        {label.icon}
      </div>
      <div class="overlay__word">{label.word}</div>

      {person && (
        <div class="overlay__person">
          <div class="overlay__name">
            {person.firstName} {person.lastName}
          </div>
          <div class="overlay__meta">
            {[person.jamaat, person.extId].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}

      {reason && <div class="overlay__reason">{reason}</div>}

      {/* Ein lokal entschiedenes Ergebnis ist nicht autoritativ. Das muss
          die Duty-Kraft sehen, damit sie im Zweifel nachfragen kann. */}
      {response.offline && <div class="overlay__badge">Offline geprüft</div>}

      {response.reason === 'already_checked_in' && response.minutesSinceLastCheck !== undefined && (
        <div class="overlay__hint">
          vor {response.minutesSinceLastCheck} Min. schon einmal eingelassen
        </div>
      )}

      {/* Gegen den Wettlauf: es steht immer dabei, wonach gesucht wurde. */}
      <div class="overlay__query">Eingabe: {phase.query}</div>

      {response.candidates && response.candidates.length > 0 && (
        <CandidateList candidates={response.candidates} onSelect={onConfirm} />
      )}

      {/* Auch GRÜN wird quittiert. Ohne den Knopf gab sich die Anzeige
          nach zwei Sekunden selbst frei, und die Karte lag da meist noch
          im Bild, was die nächste Prüfung auslöste. */}
      <div class="overlay__actions">
        <button class="btn btn--primary" onClick={onDismiss} autofocus>
          Weiter
        </button>
      </div>
    </div>
  );
}
